import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import { noop } from "lodash";
import { useValidateOtp } from "@/Pages/Auth/hooks/useValidateOtp";
import { ERROR_CODES, USER_INFO_ERROR_KEYS, USER_INFO_LENGTHS } from "@/shared";

import { colors } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import useRefreshToken from "../../../../../api/hooks/useRefreshToken";
import { ApiResources } from "../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../context/alert/types";
import { useAuth } from "../../../../../context/Auth/AuthProvider";
import {
  AUTHENTICATION_TYPES,
  DISABLE_AUTHENTICATION_STEPS,
  ENABLE_AUTHENTICATION_STEPS
} from "../../../../../Pages/Auth/types";
import useResponse from "../../../../../shared/hooks/useResponse";
import { SendPassword } from "../Dialogs/SecuritySettings/TwoFA/DisableTwoFA/content";
import {
  GetSecretKey,
  SendOtpCode
} from "../Dialogs/SecuritySettings/TwoFA/EnableTwoFA/content";
import { StatusValuesProps } from "../types";

interface UseTwoFAProps {
  onCloseDialog?: () => void;
}

const useTwoFA = ({ onCloseDialog }: UseTwoFAProps = {}) => {
  const { toggleTwoFaEnable } = useAuth();
  const { t } = useTranslation();
  const { postData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const refreshToken = useRefreshToken();

  const { error, setError, validateOtp } = useValidateOtp();

  const [step, setStep] = useState(ENABLE_AUTHENTICATION_STEPS.GetSecretKey);
  const [disableStep, setDisableStep] = useState(
    DISABLE_AUTHENTICATION_STEPS.Confirmation
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [secretKey, setSecretKey] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [password, setPassword] = useState("");
  const [payload, setPayload] = useState<any>(null);

  const changeStep = (newStep: ENABLE_AUTHENTICATION_STEPS) => setStep(newStep);

  const changeDisableStep = (newStep: DISABLE_AUTHENTICATION_STEPS) =>
    setDisableStep(newStep);

  const changeOtpCode = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;

    const trimmedValue = newValue.trim();

    if (!trimmedValue) {
      setError(null);
    }

    validateOtp(event);

    if (trimmedValue.length <= USER_INFO_LENGTHS.OtpMax) {
      setOtpCode(trimmedValue);
      setPayload({
        otpCode: trimmedValue,
        Method: AUTHENTICATION_TYPES.Authenticator
      });
    }
  };

  const enterPassword = (newValue: string) => {
    setError(null);
    setPassword(newValue);
  };

  const handleTwoFaError = (
    error: AxiosError,
    setErr?: Dispatch<SetStateAction<string | null>>
  ) => {
    const errorString = USER_INFO_ERROR_KEYS.IncorrectCode;
    const unauthorized = USER_INFO_ERROR_KEYS.Unauthorized;
    const expiredCode = USER_INFO_ERROR_KEYS.CodeExpired;

    if (error.response?.status === ERROR_CODES.BadRequest) {
      return setErr ? setErr(errorString) : setError(errorString);
    }

    if (error.response?.status === ERROR_CODES.Unauthorized) {
      return handleResponse(ALERT_STATUS.Critical, expiredCode);
    }

    handleResponse(ALERT_STATUS.Critical, error?.message || unauthorized);
  };

  const getSecretKeySubmit = async () => {
    try {
      setIsLoading(true);

      const response = await postData(ApiResources.GenerateTwoFactorKey);

      setSecretKey(response?.data.key);
      changeStep(ENABLE_AUTHENTICATION_STEPS.SendOtpCode);
    } catch (error) {
      setSecretKey("");
      const err = error as AxiosError<any>;

      if (err) {
        const responseData: string = err.response?.data
          ? t(`Alert##${err.response?.data.message}`)
          : t("Alert##action failed");
        handleResponse(ALERT_STATUS.Critical, responseData);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendOtpCodeSubmit = async () => {
    try {
      setIsLoading(true);

      await postData(ApiResources.EnableTwofactor, payload);

      toggleTwoFaEnable(true);

      changeStep(ENABLE_AUTHENTICATION_STEPS.SuccessMessage);
    } catch (error) {
      handleTwoFaError(error as AxiosError<any>);
    }

    setIsLoading(false);
  };

  const sendPasswordSubmit = async () => {
    try {
      setIsLoading(true);

      await postData(ApiResources.DisableTwofactor, {
        password
      }).then(response => {
        refreshToken();
        toggleTwoFaEnable(false);

        onCloseDialog?.();
        handleResponse(
          ALERT_STATUS.Success,
          response?.data.message || t("Password##disabled two FA")
        );
      });
    } catch (error) {
      const err = error as AxiosError<any>;

      if (err.response?.status === ERROR_CODES.BadRequest) {
        return setError(t("Password##incorrect password"));
      }

      if (err) {
        const responseData: string = err.response?.data
          ? t(`Alert##${err.response?.data.message}`)
          : t("Alert##action failed");
        handleResponse(ALERT_STATUS.Critical, responseData);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const twoFAsteps: any = {
    [ENABLE_AUTHENTICATION_STEPS.GetSecretKey]: {
      hasSeparators: true,
      description: t("Password##generate key description"),
      cancelBtn: t("Button##cancel"),
      submitBtn: t("Button##continue"),
      isSubmitBtnDisabled: false,
      content: <GetSecretKey />,
      onSubmit: getSecretKeySubmit
    },
    [ENABLE_AUTHENTICATION_STEPS.SendOtpCode]: {
      hasSeparators: true,
      description: "",
      cancelBtn: t("Button##cancel"),
      submitBtn: t("Button##verify"),
      isSubmitBtnDisabled: Boolean(error) || otpCode.length < 6,
      content: (
        <SendOtpCode
          otpValue={otpCode}
          onBlur={validateOtp}
          secretKey={secretKey}
          error={error}
          isLoading={isLoading}
          changeCode={changeOtpCode}
        />
      ),
      onSubmit: sendOtpCodeSubmit
    },
    [ENABLE_AUTHENTICATION_STEPS.SuccessMessage]: {
      hasSeparators: false,
      description: t("Password##success description"),
      cancelBtn: t("Button##close"),
      submitBtn: null,
      isSubmitBtnDisabled: false,
      content: null,
      onSubmit: noop
    }
  };

  const disableTwoFASteps = {
    [DISABLE_AUTHENTICATION_STEPS.Confirmation]: {
      hasActionSeparator: false,
      title: t("Password##are you sure"),
      description: t("Password##disable description confirmation"),
      submitBtn: t("Button##yes"),
      isSubmitBtnDisabled: false,
      content: null,
      onSubmit: () =>
        changeDisableStep(DISABLE_AUTHENTICATION_STEPS.SendPassword)
    },
    [DISABLE_AUTHENTICATION_STEPS.SendPassword]: {
      hasActionSeparator: true,
      title: t("Password##two-factor authentication"),
      description: t("Password##disable description send password"),
      submitBtn: t("Button##continue"),
      isSubmitBtnDisabled: password.length === 0,
      content: (
        <SendPassword
          error={error}
          isLoading={isLoading}
          onChangePassword={enterPassword}
        />
      ),
      onSubmit: sendPasswordSubmit
    }
  };

  const enabledStatValues: StatusValuesProps = {
    color: colors.green700,
    statusString: t("Password##enabled"),
    statusLink: t("Button##disable")
  };

  const disabledStatValues: StatusValuesProps = {
    color: colors.red800,
    statusString: t("Password##disabled"),
    statusLink: t("Button##enable")
  };

  return {
    twoFAsteps,
    step,
    isLoading,
    setStep,
    setSecretKey,
    setOtpCode,
    setError,
    enabledStatValues,
    disabledStatValues,
    disableTwoFASteps,
    setPassword,
    disableStep,
    setDisableStep,
    handleTwoFaError,
    getSecretKeySubmit
  };
};

export default useTwoFA;
